import { Box } from '@data/buildingProjects/DesignSchemes/SingleBuilding/Box';
import { BoxDesignState, LayerKind } from '@visualizer/types';
import { getViewer } from 'store/viewerStore';
import * as THREE from 'three';
import { tagDesignSchemeSurfaceMaterial } from '../DesignSchemeSurfaceMaterialHelper';
import { generatePointsCloud, removeDuplicatePoints } from '../DesignSchemeUtils';
import { DesignSchemeRenderer } from '../DesignSchemeRenderer';

export class BoxRenderer extends DesignSchemeRenderer {
    private readonly boxScheme: Box;
    declare protected config: BoxDesignState;

    constructor(boxScheme: Box, config: BoxDesignState) {
        super(config);
        this.boxScheme = boxScheme;
    }

    // =====================================
    // HELPERS
    // =====================================

    private computeBoxArea(angleDeg: number, points: { x: number; y: number }[]): number {
        const rad = THREE.MathUtils.degToRad(angleDeg);
        const cos = Math.cos(-rad);
        const sin = Math.sin(-rad);
        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;

        for (const p of points) {
            const xr = p.x * cos - p.y * sin;
            const yr = p.x * sin + p.y * cos;
            minX = Math.min(minX, xr);
            maxX = Math.max(maxX, xr);
            minY = Math.min(minY, yr);
            maxY = Math.max(maxY, yr);
        }

        return Math.max(1e-6, maxX - minX) * Math.max(1e-6, maxY - minY);
    }

    private getSnapAngle(points: { x: number; y: number }[]): number {
        let bestAngle = 0;
        let bestArea = Number.POSITIVE_INFINITY;

        for (let angle = -90; angle < 90; angle += 1) {
            const area = this.computeBoxArea(angle, points);
            if (area < bestArea) {
                bestArea = area;
                bestAngle = angle;
            }
        }

        const refineStart = bestAngle - 1;
        const refineEnd = bestAngle + 1;
        for (let angle = refineStart; angle <= refineEnd; angle += 0.1) {
            const area = this.computeBoxArea(angle, points);
            if (area < bestArea) {
                bestArea = area;
                bestAngle = angle;
            }
        }

        return Number(bestAngle.toFixed(1));
    }

    // =====================================
    // MAIN GENERATION LOGIC
    // =====================================

    public updateConfig(newConfig: Partial<BoxDesignState>) {
        this.config = this.cloneConfig({ ...this.config, ...newConfig } as BoxDesignState);
    }

    // Actual mesh generation:
    async generateMesh() {
        // 1. Get active sensors and derive the full voxel cloud:
        const sensors = this.boxScheme.getSensorDatasFor(LayerKind.MAIN);
        let pointsCloud = generatePointsCloud(sensors);
        pointsCloud = removeDuplicatePoints(pointsCloud);
        if (pointsCloud.length === 0) return;

        // 2. Read alpha and prepare XY rotation (in radians):
        const transparent = this.config.transparent ?? true;
        const snapToVolume = this.config.snapToVolume ?? false;
        const alphaDeg = snapToVolume ? this.getSnapAngle(pointsCloud) : (this.config.alpha?.value ?? 0);

        // Keep renderer config aligned with derived snap angle; runtime sync persists it to store.
        if (snapToVolume && this.config.alpha && Math.abs((this.config.alpha.value ?? 0) - alphaDeg) > 1e-6) {
            this.config.alpha.value = alphaDeg;
        }

        const alphaRad = THREE.MathUtils.degToRad(alphaDeg);
        const cos = Math.cos(-alphaRad);
        const sin = Math.sin(-alphaRad);

        // 3. Rotate points in x and y, and compute bounds (max, min dimensions):
        let minXr = Number.POSITIVE_INFINITY;
        let maxXr = Number.NEGATIVE_INFINITY;
        let minYr = Number.POSITIVE_INFINITY;
        let maxYr = Number.NEGATIVE_INFINITY;
        let minZ = Number.POSITIVE_INFINITY;
        let maxZ = Number.NEGATIVE_INFINITY;

        for (const p of pointsCloud) {
            const xr = p.x * cos - p.y * sin;
            const yr = p.x * sin + p.y * cos;
            minXr = Math.min(minXr, xr);
            maxXr = Math.max(maxXr, xr);
            minYr = Math.min(minYr, yr);
            maxYr = Math.max(maxYr, yr);
            minZ = Math.min(minZ, p.z);
            maxZ = Math.max(maxZ, p.z);
        }

        // 4. Build box dimensions & center from the computed bounds:
        const width = Math.max(1e-6, maxXr - minXr);
        const depth = Math.max(1e-6, maxYr - minYr);
        const height = Math.max(1e-6, maxZ - minZ);
        const centerXr = (minXr + maxXr) * 0.5;
        const centerYr = (minYr + maxYr) * 0.5;
        const centerZ = (minZ + maxZ) * 0.5;

        // Convert rotated-space center back to world XY:
        const centerX = centerXr * Math.cos(alphaRad) - centerYr * Math.sin(alphaRad);
        const centerY = centerXr * Math.sin(alphaRad) + centerYr * Math.cos(alphaRad);

        // 5. Create solid mesh:
        const geometry = new THREE.BoxGeometry(width, depth, height);
        const material = new THREE.MeshBasicMaterial({
            color: 0x808080,
            wireframe: false,
            side: THREE.DoubleSide,
            transparent,
            opacity: transparent ? 0.5 : 1,
            polygonOffset: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        });
        tagDesignSchemeSurfaceMaterial(material, 'opaque');
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.set(centerX, centerY, centerZ);
        mesh.rotation.z = alphaRad;

        // 6. Create edge strokes:
        const edgeGeometry = new THREE.EdgesGeometry(geometry);
        const edgeMaterial = new THREE.LineBasicMaterial({
            color: 0x000000,
            depthTest: true,
            polygonOffset: true,
            polygonOffsetFactor: 3,
            polygonOffsetUnits: 1,
            linewidth: 1,
        });
        const edges = new THREE.LineSegments(edgeGeometry, edgeMaterial);
        edges.position.copy(mesh.position);
        edges.rotation.copy(mesh.rotation);

        // 7. Add both to scene:
        this.meshGroup.add(mesh);
        this.meshGroup.add(edges);
        getViewer().scene.add(this.meshGroup);
    }
}
